import React, { useState, useCallback } from 'react';
import { usePlaidLink } from 'react-plaid-link';
import axios from 'axios';
import { getAuthHeaders } from '../utils/auth';
import { toast } from 'sonner';
import { Link as LinkIcon } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PlaidLinkButton = ({ onSuccess, buttonText, buttonClassName, relinkItemId }) => {
  const [linkToken, setLinkToken] = useState(null);
  const [loading, setLoading] = useState(false);

  const onPlaidSuccess = useCallback(async (public_token, metadata) => {
    if (relinkItemId) {
      // UPDATE mode — existing access token becomes valid again, no exchange needed
      toast.success(`Reconnected ${metadata.institution?.name || 'bank'} — updating your numbers…`);
      if (onSuccess) onSuccess();
      return;
    }
    try {
      // Exchange public token for access token
      const res = await axios.post(
        `${API}/plaid/exchange-token`,
        { public_token, institution_name: metadata.institution?.name || null },
        { headers: getAuthHeaders() }
      );

      toast.success(`Successfully connected ${metadata.institution.name}`);
      if (res.data?.warning) {
        toast.warning(res.data.warning);
      }
      
      if (onSuccess) {
        onSuccess();
      }
    } catch (error) {
      console.error('Token exchange failed:', error);
      toast.error('Failed to link account');
    }
  }, [onSuccess, relinkItemId]);

  const config = {
    token: linkToken,
    onSuccess: onPlaidSuccess,
  };

  const { open, ready } = usePlaidLink(config);

  const handleClick = async () => {
    if (!linkToken) {
      setLoading(true);
      try {
        // Get link token from backend (update mode when relinking an item)
        const url = relinkItemId
          ? `${API}/plaid/items/${relinkItemId}/relink-token`
          : `${API}/plaid/link-token`;
        const response = await axios.post(url, {}, { headers: getAuthHeaders() });
        setLinkToken(response.data.link_token);
        
        // Wait a moment for state to update, then open
        setTimeout(() => {
          open();
        }, 100);
      } catch (error) {
        console.error('Failed to create link token:', error);
        toast.error('Failed to initialize Plaid Link');
      } finally {
        setLoading(false);
      }
    } else {
      open();
    }
  };

  return (
    <button
      onClick={handleClick}
      disabled={loading}
      className={buttonClassName || "bg-primary text-primary-foreground hover:bg-primary/90 uppercase tracking-widest text-xs font-bold px-8 py-4 flex items-center gap-2 disabled:opacity-50"}
      data-testid="connect-plaid-button"
    >
      <LinkIcon className="w-4 h-4" />
      {loading ? 'Initializing...' : (buttonText || 'Connect Bank Account')}
    </button>
  );
};

export default PlaidLinkButton;
